import { showToast } from '@provi/provi-components'
import * as Sentry from '@sentry/browser'
import axios from 'axios'
import { v4 as uuidv4 } from 'uuid'

import { LOCALSTORAGE_TOKENS, ROOT_URL } from '../constants'
import { history } from '../navigation/history'
import { FEATURE_FLAGS, isFeatureEnabled } from '../services/featureFlags'
import firebase from '../services/firebase'

import { logger, setLogger } from '../utils/logger'

const api = axios.create({
  baseURL: ROOT_URL.dashboard
})

const transactionId = uuidv4().replace(/-/g, '')

export const logout = expired => {
  Sentry.configureScope(scope => scope.setUser(null))
  if (expired) {
    const AllStorage = Object.keys(localStorage)
    AllStorage.forEach(item => {
      if (item !== LOCALSTORAGE_TOKENS.email) {
        localStorage.removeItem(item)
      }
    })
    history.push('/token-invalido')
  } else {
    localStorage.clear()
    window.location.href = 'https://provi.com.br'
  }
}

api.interceptors.request.use(
  config => {
    const token = localStorage.getItem(LOCALSTORAGE_TOKENS.token)
    if (token) {
      Sentry.configureScope(function(scope) {
        scope.setTag('x-request-id', transactionId)
      })
      config.headers['x-request-id'] = transactionId
      config.headers.Authorization = token
      setLogger()
    } else {
      logout(true)
    }
    return config
  },
  error => {
    Promise.reject(error)
  }
)

api.interceptors.response.use(
  response => response,
  error => {
    if (error?.response?.status !== 401) {
      logger(error)
    }
    return Promise.reject(error)
  }
)

export const getProfileData = (_token, crid = false) => {
  return new Promise(async (resolve, reject) => {
    try {
      const url = crid ? `/profile/${crid}` : '/profile'

      const { data } = await api.get(url)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getFinancing = async token => {
  return new Promise(async (resolve, reject) => {
    try {
      api.defaults.headers.common['Authorization'] = token
      const { data } = await api.get('/financing')
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getFinancingUnique = async id => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await api.get(`/financing/${id}`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getFinancingContracts = id => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await api.get(`/financing/${id}/contracts`)
      resolve(data)
    } catch (err) {
      reject(err)
    }
  })
}

export const getFinancingInvoices = id => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await api.get(`/financing/v2/${id}/invoices`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getDashboard = id => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await api.get(`/dashboard/${id}`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const allowanceSendPhoto = (crid, LoanOptionsIds, data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await api.post(`/provi-sign/${crid}/selfie`, { ...toDocumentBody(data), LoanOptionsIds })
      resolve(response)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getInvoiceUnique = ({ PaymentFlowIds }) => {
  const storage = JSON.parse(localStorage.getItem('CRID'))
  return new Promise(async (resolve, reject) => {
    try {
      const response = await api.post('/billets/create', { PaymentFlowIds, CreditRequestId: storage && storage.id })
      resolve(response)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getDuplicateInvoice = ({ InvoiceId }) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await api.post('/billets/duplicate', { InvoiceId })
      resolve(response)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const DOCUMENT_TYPES = {
  selfie: 'selfie',
  incomeProof: 'income-proof'
}

/**
 * Uploads the file straight to S3 with the presigned POST created by ms_user.
 * Returns the key, sent instead of the URL to the endpoints that store documents.
 * @param {File|Blob} file
 * @param {string} documentType DOCUMENT_TYPES
 * @returns {Promise<{ key: string }>}
 */
const uploadDocument = async (file, documentType) => {
  const { data } = await api.post('/profile/document-upload', {
    documentType,
    contentType: file.type || 'application/octet-stream'
  })

  // The signed fields must come before the file, as S3 requires.
  const form = new FormData()
  Object.entries(data.fields).forEach(([name, value]) => form.append(name, value))
  form.append('file', file)

  await axios.post(data.url, form)

  return { key: data.key }
}

/**
 * Body for the endpoints that store a document: { key } for an S3 upload or
 * { data } for a URL (legacy value or the signed preview URL).
 * @param {{ key: string } | string} document
 * @returns {{ key: string } | { data: string }}
 */
const toDocumentBody = document => (typeof document === 'string' ? { data: document } : { key: document.key })

/**
 * Legacy upload to Firebase Storage, used while the private S3 flag is off.
 * @param {File|Blob} file
 * @returns {Promise<string>} public download URL
 */
const uploadToFirebase = async file => {
  const storage = localStorage.getItem(LOCALSTORAGE_TOKENS.crid)
  const crid = JSON.parse(storage).id
  const uuid = uuidv4()
  const now = new Date().getTime()
  const storagePath = `/${crid}/${now}-${uuid}`
  const uploadRef = firebase.storage().ref(storagePath)
  await uploadRef.put(file)
  return uploadRef.getDownloadURL()
}

export const uploadImages = async (array, documentType = DOCUMENT_TYPES.selfie) => {
  let result = []
  if (array.length > 0) {
    try {
      const usePrivateS3 = await isFeatureEnabled(FEATURE_FLAGS.privateS3Documents)
      if (!usePrivateS3) await firebase.auth().signInAnonymously()

      await Promise.all(
        array.map(async item => {
          result.push(usePrivateS3 ? await uploadDocument(item, documentType) : await uploadToFirebase(item))
        })
      )
    } catch (err) {
      if (err?.response?.data?.message) {
        Sentry.addBreadcrumb({
          category: 'ErrorRequest',
          message: 'ERROR INFO ' + err,
          level: Sentry.Severity.Warning
        })
      }
      showToast()
    }
  }
  return result
}

export const getDeclarationsIsa = async id => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await api.get(`/isa/${id}`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getSelfie = async id => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await api.get(`/selfies/${id}`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getStatusContract = async envelopeId => {
  return new Promise(async (resolve, reject) => {
    try {
      const data = await api.get(`${ROOT_URL.docsign}/v1/user/contract-status/${envelopeId}`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const saveBankAccount = async body => {
  return new Promise(async (resolve, reject) => {
    try {
      const data = await api.post('/profile/bank-account', body)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getExtraInfoCrid = async crid => {
  return new Promise(async (resolve, reject) => {
    try {
      const data = await api.get(`/financing/${crid}/extra-infos`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

/**
 * Busca os dados do parceiro (IES) registrados como super property no PostHog (partnerId/partnerName).
 * Chamada disparada ao selecionar um produto na tela "Meus Produtos".
 *
 * GET /financing/:creditRequestId/partner-info → { data: { PartnerId, partnerName } }
 *
 * @param {number|string} creditRequestId - id do CreditRequest selecionado
 * @returns {Promise<{ PartnerId: number|string, partnerName: string }>}
 */
export const getPartnerInfo = async creditRequestId => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await api.get(`/financing/${creditRequestId}/partner-info`)
      resolve(data.data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getIncomeTaxUrl = async ({ fiscalYear }) => {
  try {
    const { data } = await api.get(`/financing/income-tax?fiscalYear=${fiscalYear}`)

    return data
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: 'ERROR INFO ' + err,
      level: Sentry.Severity.Warning
    })
  }
}

export const getFiscalYears = async () => {
  try {
    const { data } = await api.get('/financing/years')

    return data.data
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: 'ERROR INFO ' + err,
      level: Sentry.Severity.Warning
    })
  }
}

/**
 * @name getSimulationUpgrade
 * @param {string} crid
 */
export const getSimulationUpgrade = async crid => {
  try {
    return api.get(`provi-sign/upgrade-allowance/${crid}/simulation`)
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: `ERROR INFO ${error}`,
      level: Sentry.Severity.Warning
    })
    throw error
  }
}

/**
 * @name getFinancingSimulation
 * @param {string} crid
 */
export const getFinancingSimulation = async crid => {
  try {
    return api.get(`provi-sign/change-financing/${crid}/simulation`)
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: `ERROR INFO ${error}`,
      level: Sentry.Severity.Warning
    })
    throw error
  }
}

/**
 * @name notifyAdditionalAllowance
 * @param {string} crid
 */
export const notifyAdditionalAllowance = async crid => {
  try {
    return api.post(`provi-sign/upgrade-allowance/${crid}/notify`)
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: `ERROR INFO ${err}`,
      level: Sentry.Severity.Warning
    })
    throw err
  }
}

/**
 * @name upgradeNewAllowance
 * @param {string} crid
 */
export const upgradeNewAllowance = async crid => {
  try {
    return api.post(`/provi-sign/upgrade-allowance/${crid}`)
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: 'ERROR INFO ' + err,
      level: Sentry.Severity.Warning
    })
    throw err
  }
}

export const changeFinancingOption = async ({ crid, optionId, createRefinContract }) => {
  try {
    const endpoint = createRefinContract
      ? '/provi-sign/create-refin-contract/renegotiation'
      : '/provi-sign/update-financing/renegotiation'

    return api.post(endpoint, {
      RefinSimulationResponseId: optionId,
      CreditRequestId: crid
    })
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: 'ERROR INFO ' + err,
      level: Sentry.Severity.Warning
    })
    throw err
  }
}

/**
 * @name hasAllowanceUpgradeInProgress
 * @param {number} CreditRequestId
 */
export const hasAllowanceUpgradeInProgress = async CreditRequestId => {
  try {
    return api.get(`/in-progress/upgrade/${CreditRequestId}`)
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: 'ERROR INFO ' + err,
      level: Sentry.Severity.Warning
    })
    throw err
  }
}

export const resMedPurchaseResume = crid => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await api.get(`/course-resume/${crid}`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const resMedPurchaseInstallments = crid => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await api.get(`/resmed-installment/${crid}`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const resMedSanarPurchase = data => {
  return new Promise(async (_, reject) => {
    try {
      await api.post(`/sanar-purchase/${data.crid.id}`, data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getResMedPurchaseAddress = crid => {
  return new Promise(async (resolve, reject) => {
    try {
      const { data } = await api.get(`/get/address/${crid}`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const updateResMedPurchaseAddress = ({ crid, data }) => {
  return new Promise(async (resolve, reject) => {
    const { street, complement, district, city, state, number, zipcode } = data
    try {
      const response = await api.post(`update/address/${crid}`, {
        street,
        complement,
        neighborhood: district,
        city,
        state,
        number,
        zipcode
      })
      resolve(response)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const validateLinkedInRegistration = async () => {
  const crid = JSON.parse(localStorage.getItem('CRID'))

  if (crid?.isISA) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await api.get(`/profile/${crid.id}/linkedIn`)
        resolve(data)
      } catch (err) {
        Sentry.addBreadcrumb({
          category: 'ErrorRequest',
          message: `ERROR INFO ${err}`,
          level: Sentry.Severity.Warning
        })
        reject(err)
      }
    })
  }
}

export const insertLinkedIn = async ({ linkedInUrl, createdInUserDashboard }) => {
  const crid = JSON.parse(localStorage.getItem('CRID'))

  if (crid?.isISA) {
    return new Promise(async (resolve, reject) => {
      try {
        const data = await api.post(`/profile/${crid.id}/linkedIn`, {
          linkedInUrl,
          createdInUserDashboard
        })
        resolve(data)
      } catch (err) {
        Sentry.addBreadcrumb({
          category: 'ErrorRequest',
          message: `ERROR INFO ${err}`,
          level: Sentry.Severity.Warning
        })
        reject(err)
      }
    })
  }
}

export const changeProfileSelfie = async photoUrl => {
  const token = localStorage.getItem(LOCALSTORAGE_TOKENS.token)
  return new Promise(async (resolve, reject) => {
    try {
      api.defaults.headers.common['Authorization'] = token
      const data = await api.post('/profile/selfie', toDocumentBody(photoUrl))
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: `ERROR INFO ${err}`,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getToken = async (CreditRequestId, metadata = {}) => {
  return new Promise(async (resolve, reject) => {
    try {
      const { ProviContractId } = metadata
      const data = await api.get(`/provi-sign/${CreditRequestId}/generate/token${ProviContractId ? `/${ProviContractId}` : ''}`)
      resolve(data)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: `ERROR INFO ${err}`,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const getNotifications = async CreditRequestId => {
  try {
    const { data } = await api.get(`/dashboard/notifications/${CreditRequestId}`)

    return data
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: `ERROR INFO ${err}`,
      level: Sentry.Severity.Warning
    })

    return err
  }
}

export const getReceiptTerm = async crid => {
  try {
    const { data } = await api.post(`/receipt/term/${crid}`, {})

    return data
  } catch (err) {
    logger(err)
  }
}

export const getTypeformFlag = async (formId, CreditRequestId) => {
  try {
    const path = CreditRequestId ? `/research/${formId}/${CreditRequestId}` : `/research/${formId}`

    const { data } = await api.get(path)
    return data
  } catch (err) {
    logger(err)
  }
}

export const getReasons = async CreditRequestId => {
  try {
    const { data } = await api.get(`/cancellation/reasons/${CreditRequestId}`)

    return data
  } catch (err) {
    logger(err)
  }
}

export const getFAQ = async CreditRequestId => {
  try {
    const { data } = await api.get(`/faq/${CreditRequestId}`)

    return data
  } catch (err) {
    logger(err)
  }
}

export const getCancellation = async CreditRequestId => {
  try {
    const { data } = await api.get(`/cancellation/${CreditRequestId}`)
    return data
  } catch (error) {
    logger(error)
  }
}

export const cancellationUpdateRequest = (CreditRequestId, data) => {
  return new Promise(async (resolve, reject) => {
    try {
      const response = await api.put(`/cancellation/${CreditRequestId}`, data)
      resolve(response)
    } catch (err) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: 'ERROR INFO ' + err,
        level: Sentry.Severity.Warning
      })
      reject(err)
    }
  })
}

export const createCancellation = async (crid, body) => {
  try {
    return await api.post(`/cancellation/${crid}`, body)
  } catch (err) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: 'ERROR INFO ' + err,
      level: Sentry.Severity.Warning
    })
    throw err
  }
}

export const getRefundSummary = async crid => {
  try {
    const { data } = await api.get(`/cancellation/refund-summary/${crid}`)
    return data
  } catch (error) {
    logger(error)
  }
}

export const updateRefundInformation = async payload => {
  try {
    const { data } = await api.put('/cancellation/refund-information', payload)
    return data
  } catch (error) {
    logger(error)
  }
}

// ==========================================
// NEGOTIATION - API Integration
// ==========================================

/**
 * Transforma os dados de installments da API para o formato de cálculo do SummaryCard
 * @param {Array} installments - Array de parcelas da API
 * @param {number} interestRate - Taxa de juros
 * @returns {object} Dados formatados para calculation
 */
const buildCalculationFromInstallments = (installments, interestRate) => {
  if (!installments || installments.length === 0) {
    return null
  }

  const overdueInstallments = installments.filter(inst => inst.daysLate > 0)
  const futureInstallments = installments.filter(inst => inst.daysLate <= 0)

  const overdueTotal = overdueInstallments.reduce((sum, inst) => sum + inst.totalValue, 0)
  const futureTotal = futureInstallments.reduce((sum, inst) => sum + inst.totalValue, 0)

  const formattedRate = `${interestRate.toFixed(2).replace('.', ',')}% a.m.`

  return {
    title: 'Cálculo do saldo devedor',
    description:
      'Seu saldo devedor é o valor total do seu contrato trazido para a data de hoje, somando parcelas atrasadas com encargos e antecipando parcelas futuras com desconto.',
    totals: {
      overdue: overdueTotal,
      future: futureTotal,
      total: overdueTotal + futureTotal
    },
    installments: installments.map((inst, index) => {
      const isOverdue = inst.daysLate > 0
      const status = inst.status || (isOverdue ? 'overdue' : 'upcoming')
      return {
        id: `calc-installment-${index + 1}`,
        label: `Parcela ${index + 1}`,
        status,
        amount: inst.totalValue,
        details: {
          dueDate: inst.dueDate,
          daysOverdue: isOverdue ? inst.daysLate : undefined,
          daysLate: inst.daysLate,
          originalValue: inst.baseValue,
          remuneratoryInterest: inst.remunerativeInterest || 0,
          remuneratoryRate: formattedRate,
          defaultInterest: inst.defaultInterest || 0,
          fine: inst.fine || 0,
          presentValueDiscount: inst?.presentValueDiscount
        }
      }
    })
  }
}

/**
 * Transforma a resposta da API de simulação para o formato esperado pelo frontend
 * @param {object} apiResponse - Resposta da API POST /renegotiation/simulation
 * @returns {object} Dados formatados para o frontend
 */
const transformSimulationResponse = apiResponse => {
  // Tratar ambos os formatos: apiResponse pode ser o array diretamente ou estar em apiResponse.data
  const dataArray = Array.isArray(apiResponse) ? apiResponse : apiResponse?.data || []

  if (!dataArray || dataArray.length === 0) {
    throw new Error('Resposta da API inválida')
  }

  // Primeira opção: parcelado com entrada
  const installmentOption = dataArray.find(item => item.entryValue > 0) || dataArray[0]
  // Segunda opção: à vista (sem entrada, pagamento único)
  const cashOption = dataArray.find(item => item.entryValue === 0)

  const renegOptions = installmentOption.renegOptions || []
  const recommendedOption = renegOptions.find(opt => opt.isRecommended) || renegOptions[0]
  const interestRate = recommendedOption?.interestRate || 3.5

  // Saldo devedor anterior vem de originalRefinanceValue (não calcular mais)
  const previousOutstandingDebt = installmentOption.originalRefinanceValue || 0

  // Parcelas originais que estão sendo renegociadas
  const currentInstallments = installmentOption.installments || []

  // Valor de entrada fixo da API
  const entryValue = installmentOption.entryValue
  const entryDueDate = installmentOption.entryDueDate

  // Valor mínimo de entrada
  const minEntryValue = installmentOption.minEntryValue || Math.max(entryValue * 0.5, 50)

  // Gerar opções de entrada para o usuário escolher
  const generateEntryOptions = () => {
    const options = []

    // Opção 1: Valor mínimo (se diferente do sugerido)
    if (minEntryValue < entryValue * 0.9) {
      options.push({
        id: 'entry-min',
        value: Math.round(minEntryValue * 100) / 100,
        dueDate: entryDueDate,
        isRecommended: false,
        badge: null
      })
    }

    // Opção 2: Valor sugerido pela API (recomendado)
    options.push({
      id: 'entry-suggested',
      value: entryValue,
      dueDate: entryDueDate,
      isRecommended: true,
      badge: 'Valor sugerido'
    })

    // Opção 3: Valor intermediário (150% do sugerido, se maior que o mínimo)
    const intermediateValue = Math.round(entryValue * 1.5 * 100) / 100
    if (intermediateValue > entryValue * 1.1 && intermediateValue > minEntryValue) {
      options.push({
        id: 'entry-intermediate',
        value: intermediateValue,
        dueDate: entryDueDate,
        isRecommended: false,
        badge: null
      })
    }

    // Ordenar por valor
    return options.sort((a, b) => a.value - b.value)
  }

  const entryOptions = generateEntryOptions()

  // Construir parcelas a partir da opção recomendada usando installmentsDueDates
  // A primeira data é a entrada, então começamos do índice 1
  const buildInstallmentsFromOption = option => {
    if (!option || !option.installmentsDueDates || option.installmentsDueDates.length <= 1) {
      return []
    }

    // Pular a primeira data (entrada) e usar as demais para as parcelas
    return option.installmentsDueDates.slice(1).map((dueDate, index) => ({
      id: `inst-${index + 1}`,
      label: `${index + 1}ª parcela`,
      value: option.installmentValue,
      dueDate: dueDate
    }))
  }

  // Primeira data de parcela (segunda data do array, pois a primeira é a entrada)
  const firstInstallmentDueDate = recommendedOption?.installmentsDueDates?.[1] || entryDueDate

  return {
    id: installmentOption.negotiationId,
    installmentNegotiationId: installmentOption.negotiationId,
    cashNegotiationId: cashOption?.negotiationId || null,
    summary: {
      outstandingDebt: recommendedOption?.outstandingDebt || recommendedOption?.refinancingValue || 0,
      previousOutstandingDebt: previousOutstandingDebt,
      refinanceValue: recommendedOption?.refinancingValue || 0,
      originalRefinanceValue: installmentOption.originalRefinanceValue || 0,
      discountPercentage: recommendedOption?.discountPercentage || 0,
      calculation: buildCalculationFromInstallments(currentInstallments, interestRate),
      proposalExpirationDate: entryDueDate,
      interestRate: interestRate,
      cashPaymentValue: cashOption?.renegOptions?.[0]?.totalValue || 0,
      cashPaymentDiscount: cashOption?.renegOptions?.[0]?.totalValue
        ? previousOutstandingDebt - cashOption.renegOptions[0].totalValue
        : 0,
      cashPaymentDiscountPercentage: cashOption?.renegOptions?.[0]?.discountPercentage || 0,
      cashPaymentDueDate: cashOption?.entryDueDate || cashOption?.renegOptions?.[0]?.installmentsDueDates?.[0] || entryDueDate,
      minEntryValue: minEntryValue,
      entryValidation: {
        minValue: minEntryValue,
        errorMessageMin: `O valor da entrada não pode ser menor que R$ ${minEntryValue.toFixed(2).replace('.', ',')}`
      }
    },
    agreement: {
      entryValue: entryValue,
      entryDueDate: entryDueDate,
      installmentsCount: recommendedOption?.installmentsNumber || 0,
      installmentValue: recommendedOption?.installmentValue || 0,
      firstInstallmentDueDate: firstInstallmentDueDate,
      totalValue: recommendedOption?.totalValue || 0
    },
    entryOptions: entryOptions,
    installmentOptions: renegOptions.map(opt => ({
      id: `inst-${opt.installmentsNumber}`,
      installments: opt.installmentsNumber,
      installmentValue: opt.installmentValue,
      totalValue: opt.totalValue,
      refinancingValue: opt.refinancingValue,
      isRecommended: opt.isRecommended,
      firstDueDate: opt.installmentsDueDates?.[1] || opt.entryDueDate,
      installmentsDueDates: opt.installmentsDueDates || [],
      interestRate: opt.interestRate,
      discountPercentage: opt.discountPercentage,
      entryDueDate: opt.entryDueDate
    })),
    installments: [
      {
        id: 'inst-entry',
        label: 'Entrada',
        value: entryValue,
        dueDate: entryDueDate
      },
      ...buildInstallmentsFromOption(recommendedOption).slice(0, 3) // Mostrar apenas as 3 primeiras parcelas
    ],
    // Dados brutos da API para referência
    _raw: {
      installmentOption,
      cashOption
    }
  }
}

/**
 * Obtém dados de simulação de negociação
 * @param {string} crid - Credit Request ID
 * @param {number} [upFront] - Valor de entrada customizado (opcional)
 * @returns {Promise<object>}
 */
export const getNegotiationSimulation = async (crid, upFront) => {
  try {
    const body = { creditRequestId: crid }
    if (upFront !== undefined && upFront !== null) {
      body.upFront = upFront
    }
    const { data } = await api.post('/renegotiation/simulation', body)
    return transformSimulationResponse(data)
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: `ERROR INFO ${error}`,
      level: Sentry.Severity.Warning
    })
    throw error
  }
}

/**
 * Obtém dados de assinatura e status de assinaturas pendentes via invoices v2
 * @param {string} crid - Credit Request ID
 * @returns {Promise<object>}
 */
export const getNegotiationAwaitingSignature = async crid => {
  try {
    const { data } = await api.get(`/financing/v2/${crid}/invoices`)
    const renegotiation = data?.data?.renegotiation || {}
    const signatureStatus = renegotiation.signatureStatus || {}
    const recipients = signatureStatus.recipients || []

    const studentRecipient = recipients.find(r => r.signer === 'User') || {}
    const guarantorRecipient = recipients.find(r => r.signer === 'Guarantor')
    const studentInfo = signatureStatus?.user || {}
    const guarantorInfo = signatureStatus?.guarantor || {}

    return {
      id: `neg-await-${crid}`,
      summary: {
        outstandingDebt: renegotiation.outstandingDebt || 0,
        previousOutstandingDebt: renegotiation.previousOutstandingDebt || 0,
        refinanceValue: renegotiation.refinanceValue || 0,
        discountPercentage: renegotiation.discountPercentage || 0,
        calculation: renegotiation.calculation || null,
        proposalExpirationDate: renegotiation.proposalExpirationDate || null,
        interestRate: renegotiation.interestRate || 0
      },
      agreement: {
        entryValue: renegotiation.entryValue || 0,
        entryDueDate: renegotiation.entryDueDate || null,
        installmentsCount: renegotiation.numOfInstallments || 0,
        installmentValue: renegotiation.installmentValue || 0,
        firstInstallmentDueDate: renegotiation.firstInstallmentDueDate || null,
        totalValue: renegotiation.totalValue || 0
      },
      installments: renegotiation.installments || [],
      termUrl: renegotiation.termUrl || null,
      signatureStatus,
      student: {
        name: studentInfo.fullName || 'NO-NAME',
        cpf: studentInfo.CPF || 'NO-CPF',
        email: studentInfo.email || 'NO-EMAIL',
        status: studentRecipient.signed ? 'completed' : 'pending'
      },
      guarantor: guarantorRecipient
        ? {
            name: guarantorInfo.fullName || 'NO-NAME',
            cpf: guarantorInfo.CPF || 'NO-CPF',
            email: guarantorInfo.email || 'NO-EMAIL',
            status: guarantorRecipient.signed ? 'completed' : 'pending'
          }
        : null
    }
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: `ERROR getNegotiationAwaitingSignature: ${error}`,
      level: Sentry.Severity.Warning
    })
    throw error
  }
}

const PAYMENT_RETRY_MAX_ATTEMPTS = 10
const PAYMENT_RETRY_DELAY_MS = 2500

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

const hasPaymentData = payment => payment && (payment.pixQrCodeText || payment.barCode || payment.boleto)

/**
 * Obtém dados de pagamento da renegociação com retry automático.
 * Se o backend ainda não gerou o pagamento (payment nulo), tenta até
 * 5 vezes com intervalo linear de 3 s entre tentativas.
 * @param {string} crid - Credit Request ID
 * @returns {Promise<object>}
 */
export const getNegotiationPayment = async crid => {
  let lastPayment = null

  for (let attempt = 1; attempt <= PAYMENT_RETRY_MAX_ATTEMPTS; attempt++) {
    try {
      const { data } = await api.get(`/renegotiation/payment/${crid}`)
      const payment = data?.data?.payment || data?.payment || null

      if (hasPaymentData(payment)) {
        return buildPaymentResult(crid, payment)
      }

      lastPayment = payment
    } catch (error) {
      Sentry.addBreadcrumb({
        category: 'ErrorRequest',
        message: `ERROR getNegotiationPayment attempt ${attempt}/${PAYMENT_RETRY_MAX_ATTEMPTS}: ${error}`,
        level: Sentry.Severity.Warning
      })

      if (attempt === PAYMENT_RETRY_MAX_ATTEMPTS) throw error
    }

    if (attempt < PAYMENT_RETRY_MAX_ATTEMPTS) {
      await delay(PAYMENT_RETRY_DELAY_MS)
    }
  }

  return buildPaymentResult(crid, lastPayment || {})
}

const buildPaymentResult = (crid, payment) => ({
  id: `neg-pay-${crid}`,
  paymentType: 'installments',
  summary: {
    outstandingDebt: 0,
    previousOutstandingDebt: 0,
    refinanceValue: 0,
    discountPercentage: 0,
    calculation: null,
    proposalExpirationDate: null,
    interestRate: 0
  },
  agreement: {
    entryValue: payment.amount ? parseFloat(payment.amount) : 0,
    entryDueDate: payment.dueDate || null,
    paymentDueDate: payment.dueDate || null,
    installmentsCount: 0,
    installmentValue: 0,
    firstInstallmentDueDate: null,
    totalValue: 0
  },
  paymentMethods: {
    pix: {
      qrCodeBase64: null,
      qrCodeUrl: payment.pixQrCode || null,
      copyPasteCode: payment.pixQrCodeText || payment.pixQrCode || ''
    },
    boleto: {
      barcode: payment.barCode || '',
      boletoUrl: payment.boleto || ''
    }
  },
  InvoiceId: payment.InvoiceId ?? null
})

/**
 * Verifica se a fatura foi paga (polling)
 * @param {string|number} invoiceId - ID da fatura
 * @returns {Promise<{ paid: boolean, ... }>}
 */

export const checkInvoicePaymentStatus = async invoiceId => {
  try {
    const { data } = await api.get(`/financing/invoices/check/${invoiceId}`)
    return data?.data ?? data ?? {}
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: `ERROR checkInvoicePaymentStatus: ${error}`,
      level: Sentry.Severity.Warning
    })
    throw error
  }
}

/**
 * Assina termo de negociação
 * @param {number} creditRequestId - Credit Request ID
 * @param {string} device - User agent string do navegador
 * @returns {Promise<object>} Resposta com signatureStatus e payment opcional
 */
export const signNegotiationTerm = async (creditRequestId, device) => {
  try {
    const { data } = await api.post('/renegotiation/signature', {
      creditRequestId,
      device
    })
    return data
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: `ERROR signNegotiationTerm: ${error}`,
      level: Sentry.Severity.Warning
    })
    throw error
  }
}

/**
 * Aceita proposta de negociação
 * @param {object} params - Parâmetros da negociação
 * @param {number} params.creditRequestId - Credit Request ID
 * @param {number} params.upFront - Valor de entrada
 * @param {string} params.negotiationId - ID da negociação
 * @param {number} params.installmentNumber - Número de parcelas (0 para quitação)
 * @returns {Promise<object>} Resposta com payment opcional (presente apenas em quitação)
 */
export const acceptNegotiationProposal = async ({ creditRequestId, upFront, negotiationId, installmentNumber }) => {
  try {
    const { data } = await api.post('/renegotiation/accept', {
      creditRequestId,
      upFront,
      negotiationId,
      installmentNumber
    })
    return data
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'ErrorRequest',
      message: `ERROR acceptNegotiationProposal: ${error}`,
      level: Sentry.Severity.Warning
    })
    throw error
  }
}
